/**
 * @fileoverview Adding a crop from What to plant (E19): which row's sheet is open, the add itself, and what each row
 * shows after it (tick + Added, Open it ›, the server's line under its card).
 *
 * A row's "+ Add" only opens the sheet (rule 7: the add happens on the sheet's button, beside its Cancel). The sheet's
 * button sends the crop, the type on a type's row (E24) and the bed the server named (POST /plants/what-to-plant/add);
 * the server makes the Planning
 * plant and answers with its words (the line naming the bed, the See them page's sub line worked out again). Nothing is
 * counted or decided here: the rows added this visit are UI state only, kept until the page is left.
 *
 * After an add every answer the app holds is marked stale without being read again now (a new plant can change the
 * plants, the beds, Today, the outlook, the seasons and both What to plant answers): each is read afresh the next time
 * a screen shows it, and the row just added stays in place while she is on this page. No key is named here, so the
 * app's value files are not read (scripts/check-value-files).
 *
 * Where: WhatToPlantPage and WhatToPlantBrowsePage.
 *
 * @module useAddCrop
 */

import React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { addCropFromWhatToPlant, type WhatToPlantAdded, type WhatToPlantCropRow } from '@/shared/api/whatToPlant';

/** What one add left: the new plant and the server's words. */
export interface AddedCrop {
  /** The new plant, for "Open it ›". */
  plantObjId: number;
  /** The line under the card: the bed it went to. */
  line: string;
  /** The See them page's sub line after it. */
  sub: string;
}

/** What the pages are handed. */
export interface AddCropState {
  /** The row whose sheet is open, or null. */
  asking: WhatToPlantCropRow | null;
  /** Opens a row's sheet. */
  ask: (row: WhatToPlantCropRow) => void;
  /** Closes the sheet without adding (Cancel, ×, a tap outside). */
  cancel: () => void;
  /** Adds the asked row's crop to the bed named. */
  confirm: (bedId: number | null) => void;
  /** True while the add is on its way. */
  adding: boolean;
  /** The add's failure, or null. */
  error: unknown;
  /** The rows added this visit, by row key ("c:12", "t:224"). */
  added: ReadonlyMap<string, AddedCrop>;
  /** The row added last: only its card carries the line naming the bed. */
  lastKey: string | null;
}

/**
 * The add, the sheet and the rows added this visit.
 *
 * @returns The state and its actions.
 */
export function useAddCrop(): AddCropState {
  const { i18n: { language } } = useTranslation();
  const queryClient = useQueryClient();
  const [asking, setAsking] = React.useState<WhatToPlantCropRow | null>(null);
  const [added, setAdded] = React.useState<ReadonlyMap<string, AddedCrop>>(new Map());
  const [lastKey, setLastKey] = React.useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: ({ row, bedId }: { row: WhatToPlantCropRow; bedId: number | null }) => addCropFromWhatToPlant(row.cropId, row.plantId, bedId, language),
    onSuccess: (answer: WhatToPlantAdded, { row }) => {
      setAdded((before) => new Map(before).set(row.key, { plantObjId: answer.plantObjId, line: answer.line, sub: answer.sub }));
      setLastKey(row.key);
      setAsking(null);
      // Everything stale, nothing read again now: the row just added stays where it is until she leaves the page.
      void queryClient.invalidateQueries({ refetchType: 'none' });
    },
  });

  /** Opens a row's sheet, clearing an earlier failure. */
  const ask = React.useCallback((row: WhatToPlantCropRow) => { mutation.reset(); setAsking(row); }, [mutation]);
  /** Closes the sheet; an add on its way still lands. */
  const cancel = React.useCallback(() => setAsking(null), []);
  /** Sends the add for the row the sheet asks about. */
  const confirm = React.useCallback((bedId: number | null) => {
    if (asking) mutation.mutate({ row: asking, bedId });
  }, [asking, mutation]);

  return {
    asking, ask, cancel, confirm, adding: mutation.isPending, error: mutation.error ?? null, added, lastKey,
  };
}
